import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Skeleton } from "@/components/ui/skeleton";
import { ListingControls } from "@/components/ListingControls";
import { BrandFilter } from "@/components/BrandFilter";
import { useResponsive, gridClass } from "@/context/ResponsiveContext";

const DEFAULT = { stock: "all", sort: "relevance", brands: [] };

// Shared product listing: stock filter, price sort and (optional) brand tick boxes — all applied server-side.
export default function ProductListing({ params, withBrands = false, testid, empty, noun = "product" }) {
  const key = JSON.stringify(params);
  const [view, setView] = useState(DEFAULT);
  const [counts, setCounts] = useState(null);
  const [facets, setFacets] = useState([]);
  const [products, setProducts] = useState(null);
  const rd = useResponsive();
  const sidebar = withBrands && facets.length >= 2;
  const Grid = ({ children, testid: t }) => <div data-testid={t} className={gridClass(rd, sidebar)}>{children}</div>;

  useEffect(() => { setView(DEFAULT); setFacets([]); }, [key]);

  useEffect(() => {
    const brands = view.brands.length ? { brands: view.brands.join("|") } : {};
    setCounts(null);
    api.get("/products/counts", { params: { ...params, ...brands, facets: withBrands && !facets.length } })
      .then((r) => { setCounts(r.data); if (r.data.brands) setFacets(r.data.brands); }).catch(() => setCounts({ all: 0, in: 0, out: 0 }));
    setProducts(null);
    api.get("/products", { params: { ...params, ...brands, ...(view.stock !== "all" ? { stock: view.stock } : {}), ...(view.sort !== "relevance" ? { sort: view.sort } : {}) } })
      .then((r) => setProducts(r.data)).catch(() => setProducts([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, view]);

  const baseEmpty = counts && counts.all === 0 && !view.brands.length;
  if (baseEmpty) return empty;
  const shown = counts ? counts[view.stock] : null;
  const body = (
    <div className="min-w-0 flex-1">
      {counts && <ListingControls value={view} onChange={setView} counts={counts} />}
      <p className="text-sm text-muted-foreground my-5" data-testid={`${testid}-count`}>
        {shown === null ? "Loading…" : `${shown} ${noun}${shown === 1 ? "" : "s"}${products && shown > products.length ? ` · showing first ${products.length}` : ""}`}
      </p>
      {!products ? <Grid>{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-80 rounded-xl" />)}</Grid>
        : products.length === 0 ? <p className="text-center py-16 text-muted-foreground" data-testid={`${testid}-filter-empty`}>No products match these filters.</p>
          : <Grid testid={`${testid}-grid`}>{products.map((p) => <ProductCard key={p.id} product={p} />)}</Grid>}
    </div>
  );
  if (!withBrands || facets.length < 2) return body;
  return (
    <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
      <BrandFilter facets={facets} picked={view.brands} onChange={(brands) => setView({ ...view, brands })} />
      {body}
    </div>
  );
}
